import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { Section } from "./_Modals/newSectionModalInterfaces";
import { useAppSelector } from "../../../Store";
import { useMemo, useState } from "react";
import { Graph_QL } from "../../../Services";
import { useDeviceInfo } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useDeviceInfo";
import { Button, PrimaryPopover } from "../..";

// TODO
import getBorderRadius from "../../_Components/getBorderRadius";
import { ButtonTypes } from "../../../Definitions";

interface NewSectionModalLvl3_Props {
  section: Section;
}

export default function NewSectionModalLvl3({section}:NewSectionModalLvl3_Props) {
  const { t } = useTranslation()
  const [, setIsOpen] = useState<boolean>(false)
  const [isExpanded, setExpand] = useState<boolean>(false)
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);
  const { data: deviceInfoState } = useDeviceInfo(connection);

  // Get devices to find i/o name
  const deviceInfo = useMemo(() => {
    if (deviceInfoState[domain]) {
      return deviceInfoState[domain].data.map((deviceInfo) => ({id: deviceInfo.device_id, name: deviceInfo.name, systemId: deviceInfo.system_assignment, type: 1}))
    }
    return []
  }, [deviceInfoState, domain])

  // #region | BorderRadius
  const gridDimensions = useMemo(() => {
    const columns = section.columns
    const rows = section.rows ?? 0

    return {
      columns,
      rows
    }
  }, [section.columns, section.rows])
  
  const parseGridArea = (area: string): [number, number] => {
    const [rowStart, colStart] = area.split("/").map(str => parseInt(str, 10));
    return [rowStart - 1, colStart - 1]; // 0-indekseret
  };
  // #endregion

  // #region | Pen View
  const penView = useMemo(() => {
    const views: JSX.Element[] = [];
  
    Array.from(section.pens).forEach((currentPen) => {
      const [row, col] = currentPen.visual ? parseGridArea(currentPen.visual) : [0, 0];
      const borderRadius = !mobileXs ? getBorderRadius({ row, col, columns: gridDimensions.columns, rows: gridDimensions.rows }) : undefined;
  
      const penElement: JSX.Element[] = [];
      const popOverElement: JSX.Element[] = [];
  
      const name = currentPen.name ?? "";
      penElement.push(
        <div key={'name_' + currentPen.penId} style={{whiteSpace: "nowrap", gridArea: "1/1/2/2", display: "flex", alignItems: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary}}>
          {`${t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })}: `} <b>{name}</b>
        </div>
      );
  
      const ioBoard = deviceInfo.find((io) => io.id === currentPen.io)?.name ?? "";
      penElement.push(
        <div key={'ioBoard_' + currentPen.penId} style={{whiteSpace: "nowrap", gridArea: "1/2/2/3", display: "flex", alignItems: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary}}>
          {`${t("devices.aicoIo", { defaultValue: "I/O" })}: `} <b>{ioBoard}</b>
        </div>
      );
  
      popOverElement.push(
        <p key={'feederOutput_' + currentPen.penId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.feederOutput", {defaultValue: "Feeder Output"})}: ${currentPen.feederOutput ?? ""}`}
        </p>
      );

      if (currentPen.waterOutput != null)
      popOverElement.push(
        <p key={'waterOutput_' + currentPen.penId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.waterOutput", {defaultValue: "Water Output"})}: ${currentPen.waterOutput ?? ""}`}
        </p>
      );

      if (currentPen.activatorInput != null)
      popOverElement.push(
        <p key={'activatorInput_' + currentPen.penId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.activatorInput", {defaultValue: "Activator Input"})}: ${currentPen.activatorInput ?? ""}`}
        </p>
      );
  
      views.push(
        <div key={'pen_' + currentPen.penId} style={{
          gridArea: currentPen.visual,
          backgroundColor: colors.greyLightSecondary,
          display: "grid",
          padding: "0px 10px",
          gridTemplateColumns: isExpanded ? "repeat(2, 1fr)" : "1fr 2fr 20px",
          gridTemplateRows: isExpanded ? "30px 1fr" : "30px",
          gap: "0px 10px",
          borderRadius
        }}>
          {penElement}
          <div style={{ gridArea: isExpanded ? "2/1/3/3" : "1/3/2/4", display: "flex", alignItems: "center", borderTop: isExpanded ? "2px solid "+colors.greyTertiary : "none" }}>
            {isExpanded ? <div style={{display: "flex", flexDirection: "column", gap: "5px", padding: "10px 0px"}}>{popOverElement}</div>
            : (<PrimaryPopover
                content={
                  <div onClick={() => setIsOpen(true)} style={{ cursor: "pointer", display: "flex" }}>
                    <glyphs.Note fillColor={colors.greyDarkPrimary} />
                  </div>
                }
                overrides={{ popoverBgColor: colors.blackPrimary }}
                popoverHeader={`${t("devices.ioSpecifications", {defaultValue: "i/o specifications"})}`}
                popoverContent={
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {popOverElement}
                  </div>
                }
              />)
            }
          </div>
        </div>
      );
    });
  
    return views;
  }, [section.pens, mobileXs, gridDimensions.columns, gridDimensions.rows, t, deviceInfo, isExpanded]);
  // #endregion

  return ( <>
    <div style={{position: "absolute", top: "185px", right: "45px"}}>
      <Button
        content={isExpanded ? t("locationFeederPages.sections.collapse", {defaultValue: "collapse"}) : t("locationFeederPages.sections.expand", {defaultValue: "expand"})}
        buttonType={ButtonTypes.textButton}
        endEnhancer={glyphs.Arrow({rotate: isExpanded ? 90 : -90, size: "15px", transparency: isExpanded ? 1 : 0.3 })}
        onClick={() => setExpand(!isExpanded)}
      />
    </div>
    <div style={{
      display: !mobileXs ? "grid" : "flex",
      flexDirection: "column",
      gridTemplateRows: !mobileXs ? `repeat(${section.rows}, 1fr)` : "none",
      gridTemplateColumns: !mobileXs ? `repeat(${section.columns}, 1fr)` : "none",
      gap: "10px",
      borderRadius: "10px",
      overflow: "hidden"
    }}>
        {penView}
      </div>
  </> );
}